import { useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Progress } from "@/modules/shared/ui/components/progress"
import {
    Users,
    ClipboardList,
    Calendar,
    MapPin,
    Briefcase,
    ChevronRight,
    Clock,
    CheckCircle,
    AlertCircle,
    Phone,
    Video,
    FileText,
    Award,
    Filter
} from "lucide-react"
import { useApplications } from "@/modules/recruitment/hooks/useApplications"
import { ApplicationDto } from "@/modules/recruitment/infrastructure/recruitment-service"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import { getDaysAgo } from "@/lib/helpers"


const stageDisplayNames: { [key: string]: string } = {
    "Applied": "Applied",
    "PhoneScreen": "Phone Screen",
    "Interview": "Interview",
    "TechnicalTest": "Technical Test",
    "Offer": "Offer",
    "Hired": "Hired",
    "Rejected": "Rejected"
}

const stageColors: { [key: string]: string } = {
    "Applied": "bg-blue-100 text-blue-700",
    "PhoneScreen": "bg-purple-100 text-purple-700",
    "Interview": "bg-orange-100 text-orange-700",
    "TechnicalTest": "bg-indigo-100 text-indigo-700",
    "Offer": "bg-green-100 text-green-700",
    "Hired": "bg-emerald-100 text-emerald-700",
    "Rejected": "bg-red-100 text-red-700"
}

const stageProgress: { [key: string]: number } = {
    "Applied": 15,
    "PhoneScreen": 30,
    "Interview": 50,
    "TechnicalTest": 65,
    "Offer": 80,
    "Hired": 100,
    "Rejected": 0
}

const stageIcons: { [key: string]: React.ComponentType<{ className?: string }> } = {
    "Applied": FileText,
    "PhoneScreen": Phone,
    "Interview": Video,
    "TechnicalTest": FileText,
    "Offer": Award,
    "Hired": CheckCircle,
    "Rejected": AlertCircle
}

export default function ApplicationsPage() {
    const { data: applications = [], isLoading: loading } = useApplications()
    const { vacancies = [] } = useVacancies()

    const [stageFilter, setStageFilter] = useState<string>("all")
    const [jobFilter, setJobFilter] = useState<string>("all")

    const filteredApplications = applications.filter((app: ApplicationDto) => {
        const stageMatch = stageFilter === "all" || app.currentStage === stageFilter
        const jobMatch = jobFilter === "all" || app.jobId === jobFilter
        return stageMatch && jobMatch
    })

    const uniqueJobs = vacancies.map((job: { id: string; title: string }) => ({ id: job.id, title: job.title }))

    const getStageIcon = (stage: string) => {
        const IconComponent = stageIcons[stage as keyof typeof stageIcons]
        return IconComponent ? <IconComponent className="w-4 h-4" /> : null
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
            </div>
        )
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">All Applications</h1>
                    <p className="text-muted-foreground mt-1">Manage and track all candidate applications</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Export Data
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <Users className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Applications</p>
                                <p className="font-bold text-xl">{applications.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-50 rounded-lg">
                                <Clock className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">In Progress</p>
                                <p className="font-bold text-xl">
                                    {applications.filter(app => !["Hired", "Rejected"].includes(app.currentStage)).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-50 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Hired</p>
                                <p className="font-bold text-xl">
                                    {applications.filter(app => app.currentStage === "Hired").length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card border border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-50 rounded-lg">
                                <Award className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Avg Score</p>
                                <p className="font-bold text-xl">
                                    {applications.length > 0
                                        ? `${Math.round(applications.reduce((sum, app) => sum + (app.matchScore || 0), 0) / applications.length)}%`
                                        : "N/A"}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <Button
                    variant={stageFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("all")}
                >
                    All ({applications.length})
                </Button>
                <Button
                    variant={stageFilter === "Applied" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("Applied")}
                >
                    Applied ({applications.filter(app => app.currentStage === "Applied").length})
                </Button>
                <Button
                    variant={stageFilter === "PhoneScreen" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("PhoneScreen")}
                >
                    Phone Screen ({applications.filter(app => app.currentStage === "PhoneScreen").length})
                </Button>
                <Button
                    variant={stageFilter === "Interview" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("Interview")}
                >
                    Interview ({applications.filter(app => app.currentStage === "Interview").length})
                </Button>
                <Button
                    variant={stageFilter === "Offer" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("Offer")}
                >
                    Offer ({applications.filter(app => app.currentStage === "Offer").length})
                </Button>
                <Button
                    variant={stageFilter === "Hired" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStageFilter("Hired")}
                >
                    Hired ({applications.filter(app => app.currentStage === "Hired").length})
                </Button>
            </div>

            {/* Job Filter */}
            <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="all">All Jobs</option>
                    {uniqueJobs.map((job: { id: string; title: string }) => (
                        <option key={job.id} value={job.id}>
                            {job.title} ({applications.filter(app => app.jobId === job.id).length})
                        </option>
                    ))}
                </select>
            </div>

            {/* Application Cards */}
            <div className="grid grid-cols-1 gap-4">
                {filteredApplications.map((application) => {
                    const candidateName = (application.candidate?.firstName && application.candidate?.lastName)
                        ? `${application.candidate.firstName} ${application.candidate.lastName}`
                        : "Unknown"
                    const candidateEmail = application.candidate?.email || ""
                    const candidateLocation = application.candidate?.location || "Not specified"
                    const jobTitle = application.vacancy?.title || "Unknown Job"
                    const daysInStage = getDaysAgo(application.stageChangedAt || application.appliedDate)

                    return (
                        <Card key={application.id} className="bg-card border border-border hover:shadow-md transition-shadow">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    {/* Left Section - Candidate & Job Info */}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-muted-foreground">
                                                    {candidateName.split(' ').map(n => n[0]).join('')}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg text-foreground">{candidateName}</h3>
                                                <p className="text-sm text-muted-foreground">{candidateEmail}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                            <div className="flex items-center gap-1">
                                                <Briefcase className="w-4 h-4" />
                                                {jobTitle}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {candidateLocation}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                Applied {new Date(application.appliedDate).toLocaleDateString()}
                                            </div>
                                        </div>

                                        {/* Next Action */}
                                        {application.nextAction && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                                <p className="text-sm text-blue-700 font-medium">Next Action:</p>
                                                <p className="text-sm text-blue-600">{application.nextAction}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right Section - Stage & Score */}
                                    <div className="flex items-center gap-6">
                                        {/* Stage Info */}
                                        <div className="text-center">
                                            <Badge className={stageColors[application.currentStage] || "bg-muted text-foreground"}>
                                                <div className="flex items-center gap-1">
                                                    {getStageIcon(application.currentStage)}
                                                    {stageDisplayNames[application.currentStage] || application.currentStage}
                                                </div>
                                            </Badge>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {daysInStage} days in stage
                                            </p>
                                        </div>

                                        {/* Score - show N/A if not calculated yet */}
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-foreground">
                                                {application.matchScore > 0 ? `${application.matchScore}%` : "N/A"}
                                            </div>
                                            <p className="text-xs text-muted-foreground">Match Score</p>
                                        </div>

                                        {/* Progress */}
                                        <div className="w-32">
                                            <Progress value={stageProgress[application.currentStage] || 0} className="h-2" />
                                            <p className="text-xs text-muted-foreground mt-1 text-center">
                                                {stageProgress[application.currentStage] || 0}% Complete
                                            </p>
                                        </div>

                                        {/* Action Button */}
                                        <Link to={`/recruitment/applications/${application.id}`}>
                                            <Button variant="outline" size="sm">
                                                View Details
                                                <ChevronRight className="w-4 h-4 ml-2" />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )
                })}
            </div>

            {filteredApplications.length === 0 && (
                <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No applications found</h3>
                    <p className="text-muted-foreground">Try adjusting your filters or check back later.</p>
                </div>
            )}
        </div>
    )
}
